import React from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import bannersService from '../../../api/banners';
import './BannerCarousel.css';

/**
 * BannerCarousel Component
 * 
 * Displays a carousel of banners with language-specific images
 * @param {Array} banners - Array of banner objects with multilingual image_url
 * @param {string} language - Current language code ('ua', 'en', 'ru')
 * @param {Function} onBannerClick - Callback when banner is clicked
 * @param {boolean} loading - Loading state
 * @param {string} variant - Display variant ('desktop' or 'mobile')
 */
function BannerCarousel({ banners = [], language = 'ua', onBannerClick, loading = false, variant = 'desktop' }) {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [previousIndex, setPreviousIndex] = React.useState(null);
    const [isAutoPlaying, setIsAutoPlaying] = React.useState(true);
    const [isTransitioning, setIsTransitioning] = React.useState(false);
    const [imageWidth, setImageWidth] = React.useState(0);
    const [containerWidth, setContainerWidth] = React.useState(0);
    const [isWideScreen, setIsWideScreen] = React.useState(window.innerWidth > 1200);
    const [isHovered, setIsHovered] = React.useState(false);
    const [wasHovered, setWasHovered] = React.useState(false);
    const imageRef = React.useRef(null);
    const containerRef = React.useRef(null);

    // Handle window resize for responsive behavior
    React.useEffect(() => {
        const handleResize = () => {
            setIsWideScreen(window.innerWidth > 1200);
            if (imageRef.current) {
                setImageWidth(imageRef.current.offsetWidth);
            }
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();


        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Update image dimensions when image loads
    const handleImageLoad = (e) => {
        setImageWidth(e.target.offsetWidth);
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
    };

    // Auto-play functionality
    React.useEffect(() => {
        // Don't auto-play if there's only one banner
        if (!isAutoPlaying || !banners.length || loading || banners.length === 1) {
            return;
        }

        const interval = setInterval(() => {
            // Анімовація переходу
            setIsTransitioning(true);
            setCurrentIndex((prevIndex) => {
                setPreviousIndex(prevIndex);
                return (prevIndex + 1) % banners.length;
            });
            setTimeout(() => {
                setIsTransitioning(false);
                setPreviousIndex(null);
            }, 500);
        }, 5000); // Change slide every 5 seconds

        return () => clearInterval(interval);
    }, [isAutoPlaying, banners.length, loading]);

    // Reset index when banners change
    React.useEffect(() => {
        setCurrentIndex(0);
    }, [banners]);

    const handlePrevious = () => {
        if (isTransitioning) return;
        setIsAutoPlaying(false);
        setIsTransitioning(true);
        setPreviousIndex(currentIndex);
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? banners.length - 1 : prevIndex - 1
        );
        setTimeout(() => {
            setIsTransitioning(false);
            setPreviousIndex(null);
        }, 500);
    };

    const handleNext = () => {
        if (isTransitioning) return;
        setIsAutoPlaying(false);
        setIsTransitioning(true);
        setPreviousIndex(currentIndex);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
        setTimeout(() => {
            setIsTransitioning(false);
            setPreviousIndex(null);
        }, 500);
    };

    const handleDotClick = (index) => {
        if (isTransitioning || index === currentIndex) return;
        setIsAutoPlaying(false);
        setIsTransitioning(true);
        setPreviousIndex(currentIndex);
        setCurrentIndex(index);
        setTimeout(() => {
            setIsTransitioning(false);
            setPreviousIndex(null);
        }, 500);
    };

    const handleBannerClick = (banner) => {
        if (loading || !onBannerClick) return;
        onBannerClick(banner);
    };

    if (!banners || banners.length === 0) {
        return null;
    }


    const currentBanner = banners[currentIndex];
    const imageUrl = bannersService.getBannerImageUrl(currentBanner.image_url, language);

    
    const previousBanner = previousIndex !== null ? banners[previousIndex] : null;
    const previousImageUrl = previousBanner 
        ? bannersService.getBannerImageUrl(previousBanner.image_url, language) 
        : null;

    if (!imageUrl) {
        return null;
    }

    const isDesktop = variant === 'desktop';
    const maxHeight = isDesktop ? '50px' : '100%';

    // Calculate button positions based on image width
    const buttonOffset = isWideScreen && containerWidth > 0 && imageWidth > 0
        ? Math.max(8, (containerWidth - imageWidth) / 2 + 4)
        : 8;

    // Responsive sizes for wide screens
    const buttonSize = isWideScreen ? 'small' : 'small';
    const buttonPadding = isWideScreen ? '2px' : '8px';
    const dotSize = isWideScreen ? 6 : 8;
    const dotGap = isWideScreen ? 0.5 : 1;
    const dotPadding = isWideScreen ? '2px 6px' : '4px 8px';

    return (
        <Box 
            className="banner-carousel"
            onMouseEnter={() => {
                setIsHovered(true);
                setWasHovered(true);
            }}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                position: 'relative',
                width: '100%',
                pointerEvents: loading ? 'none' : 'auto',
                overflow: 'hidden'
            }}
        >
            {/* Banner Image */}
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Previous Banner (underneath, stays in place) */}
                {isTransitioning && previousImageUrl && (
                    <img
                        src={previousImageUrl}
                        alt={`Banner ${previousIndex + 1}`}
                        style={{
                            maxHeight: maxHeight,
                            width: isDesktop ? 'auto' : '100%',
                            objectFit: 'contain',
                            cursor: 'default',
                            position: 'absolute',
                            transform: 'translateY(0)',
                            zIndex: 1
                        }}
                    />
                )}
                
                {/* Current Banner (on top, sliding in from top) */}
                <img
                    ref={imageRef}
                    src={imageUrl}
                    alt={`Banner ${currentIndex + 1}`}
                    style={{
                        maxHeight: maxHeight,
                        width: isDesktop ? 'auto' : '100%',
                        objectFit: 'contain',
                        cursor: loading ? 'default' : 'pointer',
                        transform: 'translateY(0)',
                        animation: isTransitioning ? 'slideInFromTop 0.5s ease-in-out' : 'none',
                        zIndex: 2
                    }}
                    onClick={() => handleBannerClick(currentBanner)}
                    onLoad={handleImageLoad}
                />

                {/* Previous Button - only show if more than 1 banner */}
                {banners.length > 1 && wasHovered && (
                    <IconButton
                        onClick={handlePrevious}
                        sx={{
                            position: 'absolute',
                            color: '#f9a800',
                            left: buttonOffset,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 3,
                            backgroundColor: 'rgba(55, 67, 69)',
                            padding: buttonPadding,
                            minWidth: isWideScreen ? '24px' : '40px',
                            minHeight: isWideScreen ? '24px' : '40px',
                            animation: isHovered 
                                ? 'slideInFromLeft 0.3s ease-out forwards' 
                                : 'slideOutToLeft 0.3s ease-out forwards',
                            '& .MuiSvgIcon-root': {
                                fontSize: isWideScreen ? '16px' : '24px',
                            },
                            '&:hover': {
                                transform: 'translateY(-50%)',
                                color: 'rgba(55, 67, 69)',
                                backgroundColor: 'rgba(249, 168, 0)',
                            },
                        }}
                        size={buttonSize}
                    >
                        <ChevronLeft />
                    </IconButton>
                )}

                {/* Next Button - only show if more than 1 banner */}
                {banners.length > 1 && wasHovered && (
                    <IconButton
                        onClick={handleNext}
                        sx={{
                            position: 'absolute',
                            right: buttonOffset,
                            color: '#f9a800',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 3,
                            backgroundColor: 'rgba(55, 67, 69)',
                            padding: buttonPadding,
                            minWidth: isWideScreen ? '24px' : '40px',
                            minHeight: isWideScreen ? '24px' : '40px',
                            animation: isHovered 
                                ? 'slideInFromRight 0.3s ease-out forwards' 
                                : 'slideOutToRight 0.3s ease-out forwards',
                            '& .MuiSvgIcon-root': {
                                fontSize: isWideScreen ? '16px' : '24px',
                            },
                            '&:hover': {
                                transform: 'translateY(-50%)',
                                color: 'rgba(55, 67, 69)',
                                backgroundColor: 'rgba(249, 168, 0)',
                            },
                        }}
                        size={buttonSize}
                    >
                        <ChevronRight />
                    </IconButton>
                )}

            </Box>

            {/* Dots Indicator - only show if more than 1 banner */}
            {banners.length > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: dotGap,
                        marginTop: isWideScreen ? '6px' : '10px',
                        padding: dotPadding
                    }}
                >
                    {banners.map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => handleDotClick(index)}
                            sx={{
                                width: dotSize,
                                height: dotSize,
                                borderRadius: '50%',
                                backgroundColor: currentIndex === index ? '#f9a800' : 'rgba(0, 0, 0, 0.3)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: currentIndex === index ? '#f9a800' : 'rgba(0, 0, 0, 0.5)',
                                    transform: 'scale(1.2)',
                                },
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default BannerCarousel;
